import { resolveLang } from "./i18n.js";
import { getViewerTimeZone } from "./timezone.js";

export function readConfig(target, options = {}) {
  const element = typeof target === "string" ? document.querySelector(target) : target;

  if (!element) {
    throw new Error("Randevou target container not found");
  }

  const apiBaseUrl = options.apiBaseUrl || element.dataset.apiBaseUrl || getQueryParam("apiBaseUrl");
  const tenantSlug = options.tenantSlug || element.dataset.tenantSlug || getQueryParam("tenantSlug");
  const publishableKey = options.publishableKey || element.dataset.publishableKey || getQueryParam("publishableKey") || null;
  // `lang` est le nom documenté ; `locale` (ancien nom) reste accepté.
  const lang = resolveLang(
    options.lang || element.dataset.lang || getQueryParam("lang") ||
    options.locale || element.dataset.locale || getQueryParam("locale")
  );
  const mode = options.mode || element.dataset.mode || getQueryParam("mode") || "inline";
  const appointmentTitle = options.appointmentTitle || element.dataset.appointmentTitle || getQueryParam("appointmentTitle") || null;
  const appointmentDuration = options.appointmentDuration || element.dataset.appointmentDuration || getQueryParam("appointmentDuration") || "30 min";
  const timezone = options.timezone || element.dataset.timezone || getQueryParam("timezone") || "Europe/Paris";
  const viewerTimezone = getViewerTimeZone();
  const accentColor = options.accentColor || element.dataset.accentColor || getQueryParam("accentColor") || "#125c9d";
  const theme = normalizeChoice(options.theme || element.dataset.theme || getQueryParam("theme"), ["light", "dark", "auto"], "light");
  const layout = normalizeChoice(options.layout || element.dataset.layout || getQueryParam("layout"), ["split", "stacked", "compact"], "split");
  const variant = normalizeChoice(options.variant || element.dataset.variant || getQueryParam("variant"), ["classic", "compact-steps"], "classic");
  const density = normalizeChoice(options.density || element.dataset.density || getQueryParam("density"), ["comfortable", "compact"], "comfortable");
  const brandName = cleanText(options.brandName || element.dataset.brandName || getQueryParam("brandName"));
  const brandLogo = cleanText(options.brandLogo || element.dataset.brandLogo || getQueryParam("brandLogo"));
  const introText = cleanText(options.introText || element.dataset.introText || getQueryParam("introText"));
  const showSummary = normalizeBoolean(options.showSummary ?? element.dataset.showSummary ?? getQueryParam("showSummary"), true);
  const showDayCount = normalizeBoolean(options.showDayCount ?? element.dataset.showDayCount ?? getQueryParam("showDayCount"), true);
  const collectPhone = normalizeBoolean(options.collectPhone ?? element.dataset.collectPhone ?? getQueryParam("collectPhone"), true);
  const collectMessage = normalizeBoolean(options.collectMessage ?? element.dataset.collectMessage ?? getQueryParam("collectMessage"), true);
  // Comportement par défaut (l'API le surcharge au chargement, source de vérité).
  const phoneRequired = normalizeBoolean(options.phoneRequired ?? element.dataset.phoneRequired ?? getQueryParam("phoneRequired"), false);
  const messageRequired = normalizeBoolean(options.messageRequired ?? element.dataset.messageRequired ?? getQueryParam("messageRequired"), false);
  // Textes du formulaire : portés par l'embed (jamais stockés côté API).
  const phoneHelpText = cleanText(options.phoneHelpText || element.dataset.phoneHelpText || getQueryParam("phoneHelpText"));
  const messageHelpText = cleanText(options.messageHelpText || element.dataset.messageHelpText || getQueryParam("messageHelpText"));
  const messagePrefill = cleanText(options.messagePrefill || element.dataset.messagePrefill || getQueryParam("messagePrefill"));
  const privacyNotice = cleanText(options.privacyNotice || element.dataset.privacyNotice || getQueryParam("privacyNotice"));
  const privacyPolicyUrl = cleanText(options.privacyPolicyUrl || element.dataset.privacyPolicyUrl || getQueryParam("privacyPolicyUrl"));

  if (!apiBaseUrl) {
    throw new Error("Randevou requires apiBaseUrl");
  }

  if (!tenantSlug) {
    throw new Error("Randevou requires tenantSlug");
  }

  return {
    element,
    apiBaseUrl: apiBaseUrl.replace(/\/$/, ""),
    tenantSlug,
    publishableKey,
    lang,
    mode,
    appointmentTitle,
    appointmentDuration,
    timezone,
    viewerTimezone,
    accentColor,
    theme,
    layout,
    variant,
    density,
    brandName,
    brandLogo,
    introText,
    showSummary,
    showDayCount,
    collectPhone,
    collectMessage,
    phoneRequired,
    messageRequired,
    phoneHelpText,
    messageHelpText,
    messagePrefill,
    privacyNotice,
    privacyPolicyUrl
  };
}

function normalizeChoice(value, allowed, fallback) {
  return allowed.includes(value) ? value : fallback;
}

function normalizeBoolean(value, fallback) {
  if (value === undefined || value === null || value === "") {
    return fallback;
  }

  if (typeof value === "boolean") {
    return value;
  }

  return !["false", "0", "no", "off"].includes(String(value).toLowerCase());
}

function cleanText(value) {
  const text = String(value || "").trim();
  return text || null;
}

function getQueryParam(name) {
  try {
    return new URLSearchParams(window.location.search).get(name);
  } catch (_error) {
    return null;
  }
}
